import { httpClient } from "@/lib/http-client/http-client";
import { API_ROUTES } from "@/shared/constants/api.routes";
import {
  ISpecificationCreateRequest,
  ISpecificationItemCreateRequest,
  ISpecificationCategoryCreateRequest,
  ISpecificationUpdateRequest,
  ISpecificationGetRequest,
  ISpecificationCategoryAndItemGetRequest,
  ISpecificationItemUpdateRequest,
  ISpecificationCategoryUpdateRequest,
  ISpecificationItemReorderRequest,
  ISpecificationItemMoveToCategoryRequest,
  ISpecificationCreateWithAIRequest,
  ISpecificationRebasePromptRequest,
  ISpecificationProductCompareRequest,
  ISpecificationCandidateProductRequest,
  ISpecificationListGetRequest,
  ISpecificationCreateWithFilesRequest,
  ISpecificationConvertToDocxRequest,
  ISpecificationShareRequest,
  ISpecificationShareHashRequest,
  ISpecificationBaseCategoriesRequest,
  ISpecificationDeleteRequest,
  ISpecificationProductExploreRequest,
  ISpecificationCloneRequest,
  ISpecificationExploreRequest,
  ISpecificationMoveExploreProductRequest,
  ISpecificationProductRequest,
  ISpecificationProductAddRequest,
  ISpecificationPinExploreProductRequest,
  ISpecificationDiscoveryNotificationRequest,
  ISpecificationPinAllExploreProductsRequest,
  ISpecificationShareMultipleRequest,
  ISpecificationClientRequirementsThresholdRequest,
  IFetchCategorySpecificScoreRequest,
  IGenerateTipItemsRequest,
  ICheckManuelStateRequest,
  IGetProductCategoriesRequest,
} from "../interface/requests/specification";
import {
  IGetSpecificationDetailRequest,
  IGetSpecificationPinnedProductsRequest,
  IGetSpecificationScoredProductsRequest,
  IGetSpecificationCandidateProductsRequest,
  IGetSpecificationContributorsRequest,
  IGetSupplierResponseDetailsRequest,
  IGetSupplierStatisticsRequest,
  IGetConflictingItemsRequest,
  IGetSpecificationProductStatsRequest,
  IGetNotFoundItemsRequest,
  IGetScreenTimeStatsRequest,
  IGetSpecificationCreditStatsRequest,
} from "../interface/requests/IReportRequest";
import {
  ISpecificationBaseCategoriesResponse,
  ISpecificationCandidateProductResponse,
  ISpecificationCategoryResponse,
  ISpecificationCategoryOrItemResponse,
  ISpecificationCreateWithFilesResponse,
  ISpecificationListItemResponse,
  ISpecificationRebasePromptResponse,
  ISpecificationResponse,
  ISpecificationShareHashResponse,
} from "../interface/responses/ISpecificationResponse";
import {
  ISpecificationDetailResponse,
  ISpecificationItemStatsResponse,
  ISpecificationPinnedProductsResponse,
  ISpecificationScoredProductsResponse,
  ISpecificationContributorsResponse,
  ISupplierResponseDetailsResponse,
  ISupplierStatisticsResponse,
  IConflictingItemsResponse,
  ISpecificationProductStatsResponse,
  INotFoundItemsResponse,
  ISpecificationScreenTimeStatsResponse,
  ISpecificationCreditStatsResponse,
} from "../interface/responses/IReportResponse";
import { ISpecificationCandidateProductsResponse } from "../interface/responses/ISpecificationCandidateProductsResponse";
import { IBaseListResponse } from "../interface/responses/IBaseResponse";
import { IExploreProduct } from "../interface/responses/ISpecificationExplore";
import { ISupabaseCompareItemAnswerResponse } from "../interface/responses/ISupabaseCompare";
import { SpecificationItemAnswerStatus } from "@/shared/enums/specification-item-answer";
import { IDiscoveryNotification } from "@/core/redux/types/notification";
import {
  ICategorySelectionRequest,
  ICategorySelectionResponse,
  ICheckManuelStateResponse,
  IClientRequirementsThresholdResponse,
  IFetchCategorySpecificScoreResponse,
  IProductCategoryExploreResponse,
} from "../interface/responses/ISpecificationResponses";

class SpecificationService {
  constructor() { }

  async createSpecification(data: ISpecificationCreateRequest) {
    const response = await httpClient.post<ISpecificationResponse>(
      API_ROUTES.Specification.root,
      data
    );
    return response;
  }

  async productCompare(data: ISpecificationProductCompareRequest) {
    const response = await httpClient.post(
      API_ROUTES.Specification.productCompare,
      data
    );
    return response;
  }

  async productExplore(data: ISpecificationExploreRequest) {
    const response = await httpClient.post(
      API_ROUTES.Specification.productExplore,
      data
    );
    return response;
  }
  async getProductExplore(
    data: ISpecificationProductExploreRequest,
    queryParams?: string
  ) {
    const response = await httpClient.get<IExploreProduct[]>(
      API_ROUTES.Specification.getProductExplore(
        data.companyId,
        data.specificationId
      ) + (queryParams || "")
    );
    return response;
  }

  async candidateProduct(data: ISpecificationCandidateProductRequest) {
    const response = await httpClient.get<
      ISpecificationCandidateProductResponse[]
    >(
      API_ROUTES.Specification.candidateProduct(
        data.companyId,
        data.specificationId
      )
    );
    return response;
  }

  async specificationItemAnswer(data: {
    companyId: string;
    specificationId: string;
  }) {
    const response = await httpClient.get<ISupabaseCompareItemAnswerResponse[]>(
      API_ROUTES.Specification.itemAnswer(data.companyId, data.specificationId)
    );
    return response;
  }

  async specificationUpdateItemAnswer(data: {
    companyId: string;
    specificationId: string;
    status: SpecificationItemAnswerStatus;
    userId: string;
    productId: string;
    itemId: string;
  }) {
    const sendData = {
      status: data.status,
      userId: data.userId,
      productId: data.productId,
      itemId: data.itemId,
    };

    const response = await httpClient.patch<
      ISupabaseCompareItemAnswerResponse[]
    >(
      API_ROUTES.Specification.itemAnswer(data.companyId, data.specificationId),
      sendData
    );
    return response;
  }

  async rebasePrompt(data: ISpecificationRebasePromptRequest) {
    const response = await httpClient.post<ISpecificationRebasePromptResponse>(
      API_ROUTES.Specification.rebasePrompt,
      data
    );
    return response;
  }

  async createWithAI(data: ISpecificationCreateWithAIRequest) {
    const response = await httpClient.post(
      API_ROUTES.Specification.createWithAI,
      data
    );
    return response;
  }

  async convertToDocx(data: ISpecificationConvertToDocxRequest) {
    const response = await httpClient.get(
      API_ROUTES.Specification.convertToDocx(
        data.companyId,
        data.specificationId
      ),
      {
        responseType: "arraybuffer",
      }
    );
    return response;
  }

  async convertToExcel(data: ISpecificationConvertToDocxRequest) {
    const response = await httpClient.get(
      API_ROUTES.Specification.convertToExcel(
        data.companyId,
        data.specificationId
      ),
      {
        responseType: "arraybuffer",
      }
    );
    return response;
  }

  async updateSpecification(data: ISpecificationUpdateRequest) {
    const response = await httpClient.patch(
      API_ROUTES.Specification.update(data.companyId, data.specificationId),
      {
        name: data.name,
        folderId: data.folderId,
      }
    );
    return response;
  }

  async createSpecificationItem(data: ISpecificationItemCreateRequest) {
    const response = await httpClient.post(
      API_ROUTES.Specification.item.root,
      data
    );
    return response;
  }

  async getSpecification(data: ISpecificationGetRequest) {
    const response = await httpClient.get(
      API_ROUTES.Specification.get(data.companyId, data.specificationId)
    );
    return response;
  }

  async deleteSpecification(data: ISpecificationDeleteRequest) {
    const response = await httpClient.delete(
      API_ROUTES.Specification.delete(data.specificationId)
    );
    return response;
  }

  async createSpecificationCategory(data: ISpecificationCategoryCreateRequest) {
    const response =
      await httpClient.post<ISpecificationCategoryResponse>(
        API_ROUTES.Specification.category.root,
        data
      );
    return response;
  }

  async getSpecificationCategoryAndItem(
    data: ISpecificationCategoryAndItemGetRequest
  ) {
    let queryParams = "";
    queryParams += data.search ? `?search=${data.search}` : "";

    if (data.categoryIds && data.categoryIds.length > 0) {
      data.categoryIds.forEach((categoryId) => {
        queryParams +=
          queryParams != ""
            ? `&categoryIds=${categoryId}`
            : `?categoryIds=${categoryId}`;
      });
    }
    if (data.userId) {
      queryParams +=
        queryParams != "" ? `&userId=${data.userId}` : `?userId=${data.userId}`;
    }
    if (data.includeCommentCount) {
      queryParams +=
        queryParams != ""
          ? `&includeCommentCount=${data.includeCommentCount}`
          : `?includeCommentCount=${data.includeCommentCount}`;
    }
    if (data.includeComments) {
      queryParams +=
        queryParams != ""
          ? `&includeComments=${data.includeComments}`
          : `?includeComments=${data.includeComments}`;
    }
    if (data.productId) {
      queryParams +=
        queryParams != ""
          ? `&productId=${data.productId}`
          : `?productId=${data.productId}`;
    }

    const response = await httpClient.get<
      Array<ISpecificationCategoryOrItemResponse>
    >(
      API_ROUTES.Specification.category.get(
        data.companyId,
        data.specificationId
      ) + queryParams
    );
    return response;
  }

  async getSpecificationList(data: ISpecificationListGetRequest) {
    const {
      companyId,
      offset = 0,
      limit = 10,
      sortBy,
      userId,
      search,
      status,
      folderId,
    } = data;

    let queryParams =
      `?offset=${offset}&limit=${limit}` +
      (sortBy ? `&sortBy=${sortBy}` : "") +
      (userId ? `&userId=${userId}` : "") +
      (search ? `&search=${search}` : "") +
      (folderId ? `&folderId=${folderId}` : "");

    if (status != null && status != undefined && status?.length > 0) {
      const statusQueryParams = status
        ?.map((status) => `&status=${status}`)
        .join("");
      queryParams += statusQueryParams;
    }
    const response = await httpClient.get(
      API_ROUTES.Specification.list(companyId) + queryParams
    );
    return response as IBaseListResponse<ISpecificationListItemResponse[]>;
  }

  async updateSpecificationItem(data: ISpecificationItemUpdateRequest) {
    let sendData = {
      description: data.description,
    } as ISpecificationItemUpdateRequest;
    sendData.score = data.score;
    if (data.categoryId) {
      sendData.categoryId = data.categoryId;
    }
    if (data.orderNo !== undefined) {
      sendData.orderNo = data.orderNo;
    }

    const response = await httpClient.patch(
      API_ROUTES.Specification.item.update(
        data.itemId,
        data.companyId,
        data.specificationId
      ),
      sendData
    );
    return response;
  }

  async updateSpecificationCategory(
    data: ISpecificationCategoryUpdateRequest,
    isSendParentId: boolean = true
  ) {
    const sendData = {
      name: data.name,
    } as any;
    if (isSendParentId) {
      if (data.parentId) {
        sendData.parentId = data.parentId;
      } else {
        sendData.parentId = null;
      }
    }
    const response = await httpClient.patch(
      API_ROUTES.Specification.category.update(
        data.categoryId,
        data.companyId,
        data.specificationId
      ),
      sendData
    );
    return response;
  }

  async reorderCategoryItem(data: ISpecificationItemReorderRequest) {
    const sendData: { beforeId?: string; afterId?: string } = {};
    if (data.beforeId) {
      sendData.beforeId = data.beforeId;
    }
    if (data.afterId) {
      sendData.afterId = data.afterId;
    }
    const response = await httpClient.put(
      API_ROUTES.Specification.item.reorder(
        data.companyId,
        data.specificationId,
        data.draggedId
      ),
      sendData
    );
    return response;
  }

  async moveCategoryItemToCategory(data: ISpecificationItemMoveToCategoryRequest) {
    const sendData = {
      parentCategoryId: data.parentCategoryId,
    };
    const response = await httpClient.put(
      API_ROUTES.Specification.item.moveToCategory(
        data.companyId,
        data.specificationId,
        data.targetId
      ),
      sendData
    );
    return response;
  }

  async deleteSpecificationCategory(categoryId: string) {
    const response = await httpClient.delete(
      API_ROUTES.Specification.category.delete(categoryId)
    );
    return response;
  }

  async deleteSpecificationItem(itemId: string) {
    console.trace("🔍 Call stack:");
    const response = await httpClient.delete(
      API_ROUTES.Specification.item.delete(itemId)
    );
    return response;
  }

  async restoreSpecificationItem(itemId: string) {
    const response = await httpClient.patch(
      API_ROUTES.Specification.item.restore(itemId)
    );
    return response;
  }

  async restoreSpecification(specificationId: string) {
    const response = await httpClient.patch(
      API_ROUTES.Specification.restore(specificationId)
    );
    return response;
  }
  async createSpecificationWithFiles(
    data: ISpecificationCreateWithFilesRequest
  ) {
    const response =
      await httpClient.post<ISpecificationCreateWithFilesResponse>(
        API_ROUTES.Specification.files,
        data
      );
    return response;
  }

  async shareSpecification(data: ISpecificationShareRequest) {
    const response = await httpClient.patch(
      API_ROUTES.Specification.share.root(data.specificationId),
      {
        isPublic: data.isPublic,
        permission: data.permission,
        isAccessAllCategories: data.isAccessAllCategories,
        categories: data.categories,
      }
    );
    return response;
  }
  // async getActiveCategories(data: { specificationId: string }) {
  //     const response = await httpClient.get(
  //         API_ROUTES.Specification.share.activeCategories(
  //             data.specificationId
  //         )
  //     );
  //     return response;
  // }
  async getShareHashSpecification(data: ISpecificationShareHashRequest) {
    const response = await httpClient.post<ISpecificationShareHashResponse>(
      API_ROUTES.Specification.hash,
      data
    );
    return response;
  }

  async getBaseCategories(data: ISpecificationBaseCategoriesRequest) {
    const response = await httpClient.get<
      ISpecificationBaseCategoriesResponse[]
    >(
      API_ROUTES.Specification.baseCategories(
        data.companyId,
        data.specificationId
      )
    );
    return response;
  }

  async copySpecification(data: ISpecificationCloneRequest) {
    const response = await httpClient.patch<{ id: string }>(
      API_ROUTES.Specification.copy(data.companyId, data.specificationId),
      data
    );
    return response;
  }
  async moveExploreProduct(data: ISpecificationMoveExploreProductRequest) {
    const response = await httpClient.post(
      API_ROUTES.Specification.moveExploreProduct,
      data
    );
    return response;
  }

  async skipSpecification(companyId: string, specificationId: string) {
    const response = await httpClient.post(
      API_ROUTES.Specification.skip(companyId, specificationId),
      {}
    );
    return response;
  }

  async pinAllExploreProducts(
    data: ISpecificationPinAllExploreProductsRequest
  ) {
    const response = await httpClient.patch(
      API_ROUTES.Specification.pinAllExploreProducts(
        data.companyId,
        data.specificationId
      ),
      data
    );
    return response;
  }

  async pinExploreProduct(data: ISpecificationPinExploreProductRequest) {
    const response = await httpClient.patch(
      API_ROUTES.Specification.explorePin(
        data.companyId,
        data.specificationId,
        data.specificationExploreId
      ),
      {
        isPinned: data.isPinned,
      }
    );
    return response;
  }
  async getProducts(data: ISpecificationProductRequest) {
    const response = await httpClient.get<IExploreProduct[]>(
      API_ROUTES.Specification.product(data.companyId, data.specificationId) +
      (data.search ? `?search=${data.search}` : "")
    );
    return response;
  }
  async compareAddProducts(data: ISpecificationProductAddRequest) {
    const response = await httpClient.patch(
      API_ROUTES.Specification.productsAdd(
        data.companyId,
        data.specificationId
      ),
      {
        productIds: data.productIds,
      }
    );
    return response;
  }

  async bulkDeleteCandidateProducts(data: ISpecificationProductAddRequest) {
    const response = await httpClient.post(
      API_ROUTES.Specification.candidateProductBulkDelete(
        data.companyId,
        data.specificationId
      ),
      {
        productIds: data.productIds,
      }
    );
    return response;
  }

  async getDiscoveryNotifications(
    data: ISpecificationDiscoveryNotificationRequest
  ) {
    const response = await httpClient.get<IDiscoveryNotification[]>(
      API_ROUTES.Specification.discoveryNotifications(
        data.companyId,
        data.specificationId
      )
    );
    return response;
  }

  async multipleDelete(ids: string[]) {
    const response = await httpClient.post(
      API_ROUTES.Specification.multipleDelete,
      {
        ids: ids,
      }
    );
    return response;
  }

  async downloadMultipleSpecification(ids: string[], companyId: string) {
    const response = await httpClient.post(
      API_ROUTES.Specification.downloadMultiple,
      {
        companyId: companyId,
        specificationIds: ids,
      },
      {
        responseType: "arraybuffer",
      }
    );
    return response;
  }
  async shareSpecificationMultiple(data: ISpecificationShareMultipleRequest) {
    const response = await httpClient.post(
      API_ROUTES.Specification.share.multiple,
      data
    );
    return response;
  }

  //Manual Specification
  async getClientRequirementsThreshold(
    data: ISpecificationClientRequirementsThresholdRequest
  ): Promise<IClientRequirementsThresholdResponse> {
    const response = (await httpClient.post(
      API_ROUTES.Specification.clientRequirementsThreshold,
      data
    )) as any;
    return response as IClientRequirementsThresholdResponse;
  }

  async selectCategory(
    data: ICategorySelectionRequest
  ): Promise<ICategorySelectionResponse> {
    const response = (await httpClient.post(
      API_ROUTES.Specification.categorySelection,
      data
    )) as any;
    return response as ICategorySelectionResponse;
  }

  async fetchCategorySpecificScore(data: IFetchCategorySpecificScoreRequest) {
    const response = await httpClient.post<IFetchCategorySpecificScoreResponse>(
      API_ROUTES.Specification.fetchCategorySpecificScore,
      data
    );
    return response;
  }

  async generateTipItems(data: IGenerateTipItemsRequest) {
    const response = await httpClient.post(
      API_ROUTES.Specification.generateTipItems,
      data
    );
    return response;
  }

  async checkManuelState(data: ICheckManuelStateRequest) {
    const response = await httpClient.post<ICheckManuelStateResponse>(
      API_ROUTES.Specification.checkManuelState,
      data
    );
    return response;
  }

  async getProductCategories(data: IGetProductCategoriesRequest) {
    const queryParams = data.search ? `?search=${data.search}` : "";
    const response = await httpClient.get<IProductCategoryExploreResponse>(
      API_ROUTES.Specification.productCategories + queryParams
    );
    return response;
  }

  async getSpecificationDetail(data: IGetSpecificationDetailRequest) {
    const response = await httpClient.get<ISpecificationDetailResponse>(
      API_ROUTES.Report.specificationDetail(data.specificationId)
    );
    return response;
  }

  async getSpecificationItemStats(data: IGetSpecificationDetailRequest) {
    const response = await httpClient.get<ISpecificationItemStatsResponse>(
      API_ROUTES.Report.specificationItemStats(data.specificationId)
    );
    return response;
  }

  async getSpecificationPinnedProducts(
    data: IGetSpecificationPinnedProductsRequest
  ) {
    const queryParams = data.limit ? `?limit=${data.limit}` : "";
    const response = await httpClient.get<ISpecificationPinnedProductsResponse>(
      API_ROUTES.Report.specificationPinnedProducts(data.specificationId) +
      queryParams
    );
    return response;
  }

  async getSpecificationScoredProducts(
    data: IGetSpecificationScoredProductsRequest
  ) {
    const queryParams = data.limit ? `?limit=${data.limit}` : "";
    const response = await httpClient.get<ISpecificationScoredProductsResponse>(
      API_ROUTES.Report.specificationScoredProducts(data.specificationId) +
      queryParams
    );
    return response;
  }

  async getSpecificationCandidateProducts(
    data: IGetSpecificationCandidateProductsRequest
  ) {
    const queryParams = data.limit ? `?limit=${data.limit}` : "";
    const response =
      await httpClient.get<ISpecificationCandidateProductsResponse>(
        API_ROUTES.Report.specificationCandidateProducts(data.specificationId) +
        queryParams
      );
    return response;
  }

  async getSpecificationContributors(
    data: IGetSpecificationContributorsRequest
  ) {
    const queryParams = new URLSearchParams();
    if (data.offset !== undefined)
      queryParams.append("offset", data.offset.toString());
    if (data.limit !== undefined)
      queryParams.append("limit", data.limit.toString());

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : "";
    const response = await httpClient.get<ISpecificationContributorsResponse>(
      API_ROUTES.Report.specificationContributors(data.specificationId) +
      queryString
    );
    return response;
  }

  async getSupplierResponseDetails(data: IGetSupplierResponseDetailsRequest) {
    const response = await httpClient.get<ISupplierResponseDetailsResponse>(
      API_ROUTES.Report.supplierResponseDetails(data.specificationId)
    );
    return response;
  }

  async getSupplierStatistics(data: IGetSupplierStatisticsRequest) {
    const response = await httpClient.get<ISupplierStatisticsResponse>(
      API_ROUTES.Report.supplierStatistics(data.specificationId)
    );
    return response;
  }

  async getSpecificationScreenTimeStats(data: IGetScreenTimeStatsRequest) {
    const queryParams = new URLSearchParams();
    queryParams.append("specificationId", data.specificationId);
    if (data.offset !== undefined) {
      queryParams.append("offset", data.offset.toString());
    }
    if (data.limit !== undefined) {
      queryParams.append("limit", data.limit.toString());
    }

    const queryString = `?${queryParams.toString()}`;
    const response =
      await httpClient.get<ISpecificationScreenTimeStatsResponse>(
        API_ROUTES.Report.screenTimeStats + queryString
      );
    return response;
  }

  async getConflictingItems(data: IGetConflictingItemsRequest) {
    const response = await httpClient.get<IConflictingItemsResponse>(
      API_ROUTES.Report.conflictingItems(data.specificationId)
    );
    return response;
  }

  async getSpecificationProductStats(
    data: IGetSpecificationProductStatsRequest
  ) {
    const response = await httpClient.get<ISpecificationProductStatsResponse>(
      API_ROUTES.Report.specificationProductStats(data.specificationId)
    );
    return response;
  }

  async getNotFoundItems(data: IGetNotFoundItemsRequest) {
    const response = await httpClient.get<INotFoundItemsResponse>(
      API_ROUTES.Report.notFoundItems(data.specificationId)
    );
    return response;
  }

  async getSpecificationCreditStats(data: IGetSpecificationCreditStatsRequest) {
    const response = await httpClient.get<ISpecificationCreditStatsResponse>(
      API_ROUTES.Report.specificationCreditStats(data.specificationId)
    );
    return response;
  }
}

export const specificationService = new SpecificationService();
export default SpecificationService;
